import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Checkbox,
  InputNumber,
  Tooltip
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const validateFieldLabel = (_, value) => {
  if (!value) {
    return Promise.reject(new Error('Please enter field label'));
  }
  if (!/^{{[a-z0-9_]+}}$/.test(value)) {
    return Promise.reject(new Error('Label must be in snake case, enclosed in double curly braces, with only lowercase letters and numbers.'));
  }
  if (value.length > 32) {
    return Promise.reject(new Error('Label must be 30 characters or fewer (excluding curly braces).'));
  }
  return Promise.resolve();
};

const validateDocumentType = (_, value) => {
  if (!value) {
    return Promise.reject(new Error('Please enter document type'));
  }
  const allowedTypes = ['Test Script', 'Test Plan', 'Requirements', 'Specifications'];
  if (!allowedTypes.includes(value)) {
    return Promise.reject(new Error('Type must be one of: Test Script, Test Plan, Requirements, Specifications.'));
  }
  return Promise.resolve();
};

const validateFieldName = (_, value) => {
  if (!value) {
    return Promise.reject(new Error('Please enter field name'));
  }
  if (!/^[A-Za-z0-9_.\- ]+$/.test(value)) {
    return Promise.reject(new Error('Name may include letters, numbers, underscores, hyphens, periods, and spaces.'));
  }
  if (value.startsWith('.') || value.endsWith('.')) {
    return Promise.reject(new Error('Name must not start or end with a period.'));
  }
  if (value.length > 30) {
    return Promise.reject(new Error('Name must be 30 characters or fewer.'));
  }
  return Promise.resolve();
};

function AddMetadataDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      onSave({
        document_type: values.documentType,
        field_label: values.fieldLabel,
        field_name: values.fieldName,
        data_type: values.dataType || '',
        required: values.isRequired || false,
        order: values.order || 0,
        is_name_field: values.isNameField || false,
        options: values.options || '',
      });

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add Metadata"
      open={open}
      onCancel={handleClose}
      onOk={handleSave}
      okText="Add"
      cancelText="Cancel"
      confirmLoading={loading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          documentType: '',
          fieldLabel: '',
          fieldName: '',
          dataType: '',
          isRequired: false,
          order: 0,
          isNameField: false,
          options: ''
        }}
      >
        <Form.Item
          label={
            <span>
              Document Type{' '}
              <Tooltip title="Allowed Types: Test Script, Test Plan, Requirements, Specifications.">
                <QuestionCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </span>
          }
          name="documentType"
          rules={[{ validator: validateDocumentType }]}
        >
          <Input placeholder="Enter document type" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Field Label{' '}
              <Tooltip title="Format: Snake case enclosed in {{ }}. Only lowercase letters and numbers, up to 30 characters.">
                <QuestionCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </span>
          }
          name="fieldLabel"
          rules={[{ validator: validateFieldLabel }]}
        >
          <Input placeholder="e.g., {{field_name}}" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Field Name{' '}
              <Tooltip title="Include letters, numbers, underscore (_), hyphen (-), and period (.). Up to 30 characters. Spaces are allowed but may cause issues in some systems.">
                <QuestionCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </span>
          }
          name="fieldName"
          rules={[{ validator: validateFieldName }]}
        >
          <Input placeholder="Enter field name" />
        </Form.Item>

        <Form.Item
          label="Data Type"
          name="dataType"
        >
          <Input placeholder="Enter data type" />
        </Form.Item>

        <Form.Item
          name="isRequired"
          valuePropName="checked"
        >
          <Checkbox>Required</Checkbox>
        </Form.Item>

        <Form.Item
          label="Order"
          name="order"
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            placeholder="Enter order number"
          />
        </Form.Item>

        <Form.Item
          name="isNameField"
          valuePropName="checked"
        >
          <Checkbox>Is Name Field</Checkbox>
        </Form.Item>

        <Form.Item
          label="Options (JSON)"
          name="options"
        >
          <Input.TextArea
            rows={4}
            placeholder="Enter options as JSON"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default AddMetadataDialog;
